import React, { ReactNode } from 'react';
import TitleWithDescription from '../TitleWithDescription/TitleWithDescription';
import ImageWithFallback from '../ImageWithFallback/ImageWithFallback';

interface TimelineSectionProps {
    title: {
        firstLine: ReactNode;
        secondLine: ReactNode;
    };
    description: React.ReactNode;
    media?: {
        type: 'image';
        src: string | string[];
        alt: string;
        fallbackSrc?: string;
    } | {
        type: 'component';
        component: React.ReactNode;
    };
    mediaPosition?: 'left' | 'right' | 'none';
    showLine?: boolean;
    extraContent?: React.ReactNode;
}

const TimelineSection: React.FC<TimelineSectionProps> = ({
    title,
    description,
    media,
    mediaPosition = 'right',
    showLine = true,
    extraContent,
}) => {
    const MediaComponent = media && (
        <div className={`w-full lg:w-1/2 flex flex-col items-center justify-${mediaPosition === 'left' ? 'end lg:pr-12' : 'start lg:pl-12'} ${mediaPosition === 'left' ? 'order-2 lg:order-1' : 'order-2'} mt-2 lg:mt-0`}>
            {media.type === 'image' ? (
                <div className={`flex flex-col space-y-4 w-full ${mediaPosition === 'left' ? 'items-end' : 'items-start'}`}>
                    {Array.isArray(media.src) ? (
                        media.src.map((srcUrl, index) => (
                            <ImageWithFallback
                                key={index}
                                src={srcUrl}
                                fallbackSrc={media.fallbackSrc || "/logo192.png"}
                                alt={`${media.alt} ${index + 1}`}
                                className="w-full lg:w-auto lg:max-w-[90%] h-auto"
                            />
                        ))
                    ) : (
                        <ImageWithFallback
                            src={media.src}
                            fallbackSrc={media.fallbackSrc || "/logo192.png"}
                            alt={media.alt}
                            className="w-full lg:w-auto lg:max-w-[90%] h-auto"
                        />
                    )}
                </div>
            ) : (
                media.component
            )}
        </div>
    );

    const TextComponent = (
        <div className={`w-full lg:w-1/2 ${mediaPosition === 'left' ? 'lg:pl-8 order-1' : 'lg:pr-8 order-1 lg:order-1'}`}>
            <TitleWithDescription
                title={title}
                description={description}
                layout="column"
            />
            {extraContent}
        </div>
    );

    return (
        <div className="relative flex flex-col lg:flex-row items-center py-2 lg:py-6">
            {/* Timeline elements */}
            <div className="hidden lg:block absolute top-[162px] left-1/2 -translate-x-1/2 w-3 h-3 bg-[#1A130F] rounded-full" />
            {showLine && (
                <div className="hidden lg:block absolute top-[162px] left-1/2 -translate-x-1/2 w-[1px] bg-[#1A130F] h-[calc(100%+48px)]" />
            )}

            {/* Content */}
            {mediaPosition === 'left' ? (
                <>
                    {MediaComponent}
                    {TextComponent}
                </>
            ) : mediaPosition === 'right' ? (
                <>
                    {TextComponent}
                    {MediaComponent}
                </>
            ) : (
                <>
                    {TextComponent}
                    <div className="hidden lg:block lg:w-1/2" />
                </>
            )}
        </div>
    );
};

export default TimelineSection; 